import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BRAND from '../brand';
import '../styles/legal.css';

const PrivacyPolicy = ({ language }) => {
  const navigate = useNavigate();
  const isArabic = language === 'ar';

  const content = isArabic ? {
    title: 'سياسة الخصوصية',
    back: 'العودة للرئيسية',
    lastUpdated: 'آخر تحديث',
    introduction: 'مقدمة',
    introText: `يرحب بك ${BRAND.ar} في سياسة الخصوصية الخاصة بنا. نحن ملتزمون بحماية خصوصيتك وضمان شفافية كاملة حول كيفية جمع واستخدام بياناتك الشخصية.`,
    
    informationWeCollect: 'المعلومات التي نجمعها',
    informationWeCollectText: 'نجمع المعلومات التالية:',
    infoList: [
      'معلومات الحساب: الاسم والبريد الإلكتروني ورقم الهاتف والموقع الجغرافي',
      'معلومات الاستخدام: سجلات الوصول وأنماط الاستخدام والأجهزة المستخدمة',
      'معلومات الدفع: تفاصيل بطاقة الائتمان (معالجة آمنة من قبل معالجات دفع موثوقة)',
      'البيانات الفنية: عنوان IP والمتصفح والنظام التشغيلي والإحداثيات الزمنية',
    ],

    howWeUseInfo: 'كيف نستخدم معلوماتك',
    howWeUseInfoText: 'نستخدم المعلومات المجمعة للأغراض التالية:',
    useList: [
      'تقديم وتحسين خدماتنا',
      'معالجة المعاملات والدفع',
      'إرسال الإخطارات والتحديثات المهمة',
      'تحليل الاستخدام وتحسين تجربة المستخدم',
      'الامتثال للالتزامات القانونية والتنظيمية',
      'منع الاحتيال والأنشطة غير القانونية',
    ],

    dataProtection: 'حماية البيانات',
    dataProtectionText: 'نتخذ إجراءات أمنية صارمة لحماية بياناتك الشخصية:',
    protectionList: [
      'تشفير SSL/TLS لجميع الاتصالات',
      'تخزين آمن للبيانات مع نسخ احتياطية منتظمة',
      'حماية كلمات المرور باستخدام خوارزميات تجزئة قوية',
      'التحديثات الأمنية المنتظمة والفحوصات',
      'سياسات صارمة لوصول الموظفين',
    ],

    thirdParties: 'مشاركة البيانات مع أطراف ثالثة',
    thirdPartiesText: 'قد نشارك معلوماتك مع الأطراف الثالثة التالية فقط:',
    thirdPartiesList: [
      'معالجات الدفع (لمعالجة المدفوعات)',
      'مقدمو خدمات البريد الإلكتروني (للإخطارات)',
      'مزودو خدمات استضافة البيانات (لتخزين آمن)',
      'السلطات القانونية (عند الضرورة القانونية)',
    ],

    cookies: 'ملفات تعريف الارتباط',
    cookiesText: 'نستخدم ملفات تعريف الارتباط لتحسين تجربتك. يمكنك التحكم في إعدادات ملفات تعريف الارتباط من خلال متصفحك. بعض الوظائف قد لا تعمل بدون ملفات تعريف الارتباط الضرورية.',

    retention: 'مدة الاحتفاظ بالبيانات',
    retentionText: 'نحتفظ بيانات المستخدم طالما حسابك نشط. يمكنك طلب حذف بياناتك في أي وقت. قد نحتفظ بنسخ احتياطية لأغراض قانونية وأمنية لمدة محدودة.',

    yourRights: 'حقوقك',
    yourRightsText: 'لديك الحقوق التالية:',
    rightsList: [
      'الوصول إلى بياناتك الشخصية',
      'تصحيح المعلومات غير الدقيقة',
      'حذف حسابك وبياناتك',
      'نقل بياناتك إلى خدمة أخرى',
      'الاعتراض على معالجة بياناتك',
      'سحب الموافقة في أي وقت',
    ],

    contact: 'التواصل معنا',
    contactText: 'إذا كان لديك أي أسئلة حول سياسة الخصوصية أو حقوقك، يرجى التواصل معنا:',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
  } : {
    title: 'Privacy Policy',
    back: 'Back to Home',
    lastUpdated: 'Last Updated',
    introduction: 'Introduction',
    introText: `Welcome to ${BRAND.en}'s Privacy Policy. We are committed to protecting your privacy and ensuring complete transparency about how we collect and use your personal data.`,
    
    informationWeCollect: 'Information We Collect',
    informationWeCollectText: 'We collect the following information:',
    infoList: [
      'Account Information: Name, email, phone number, and geographic location',
      'Usage Information: Access logs, usage patterns, and devices used',
      'Payment Information: Credit card details (processed securely by trusted payment processors)',
      'Technical Data: IP address, browser, operating system, and timestamps',
    ],

    howWeUseInfo: 'How We Use Your Information',
    howWeUseInfoText: 'We use collected information for the following purposes:',
    useList: [
      'Providing and improving our services',
      'Processing transactions and payments',
      'Sending important notifications and updates',
      'Analyzing usage and improving user experience',
      'Complying with legal and regulatory obligations',
      'Preventing fraud and illegal activities',
    ],

    dataProtection: 'Data Protection',
    dataProtectionText: 'We take strict security measures to protect your personal data:',
    protectionList: [
      'SSL/TLS encryption for all communications',
      'Secure data storage with regular backups',
      'Password protection using strong hashing algorithms',
      'Regular security updates and audits',
      'Strict employee access policies',
    ],

    thirdParties: 'Sharing Data With Third Parties',
    thirdPartiesText: 'We may share your information with the following third parties only:',
    thirdPartiesList: [
      'Payment Processors (to process payments)',
      'Email Service Providers (for notifications)',
      'Data Hosting Providers (for secure storage)',
      'Legal Authorities (when legally required)',
    ],

    cookies: 'Cookies',
    cookiesText: 'We use cookies to improve your experience. You can control cookie settings through your browser. Some features may not work without essential cookies.',

    retention: 'Data Retention',
    retentionText: 'We retain user data while your account is active. You can request data deletion at any time. We may retain backups for legal and security purposes for a limited period.',

    yourRights: 'Your Rights',
    yourRightsText: 'You have the following rights:',
    rightsList: [
      'Access your personal data',
      'Correct inaccurate information',
      'Delete your account and data',
      'Transfer your data to another service',
      'Object to data processing',
      'Withdraw consent at any time',
    ],

    contact: 'Contact Us',
    contactText: 'If you have any questions about this privacy policy or your rights, please contact us:',
    email: 'Email',
    phone: 'Phone',
  };

  return (
    <div className={`legal-page ${isArabic ? 'rtl' : 'ltr'}`} dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="legal-header">
        <button 
          onClick={() => navigate('/')} 
          className="back-button"
          aria-label={content.back}
        >
          <ArrowLeft size={20} />
          <span>{content.back}</span>
        </button>
        <h1>{content.title}</h1>
        <p className="last-updated">{content.lastUpdated}: December 16, 2025</p>
      </div>

      <div className="legal-content">
        {/* Introduction */}
        <section className="legal-section">
          <h2>{content.introduction}</h2>
          <p>{content.introText}</p>
        </section>

        {/* Information We Collect */}
        <section className="legal-section">
          <h2>{content.informationWeCollect}</h2>
          <p>{content.informationWeCollectText}</p>
          <ul className="list-items">
            {content.infoList.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </section>

        {/* How We Use Your Information */}
        <section className="legal-section">
          <h2>{content.howWeUseInfo}</h2>
          <p>{content.howWeUseInfoText}</p>
          <ul className="list-items">
            {content.useList.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </section>

        {/* Data Protection */}
        <section className="legal-section">
          <h2>{content.dataProtection}</h2>
          <p>{content.dataProtectionText}</p>
          <ul className="list-items">
            {content.protectionList.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </section>

        {/* Third Parties */}
        <section className="legal-section">
          <h2>{content.thirdParties}</h2>
          <p>{content.thirdPartiesText}</p>
          <ul className="list-items">
            {content.thirdPartiesList.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </section>

        {/* Cookies */}
        <section className="legal-section">
          <h2>{content.cookies}</h2>
          <p>{content.cookiesText}</p>
        </section>

        {/* Retention */}
        <section className="legal-section">
          <h2>{content.retention}</h2>
          <p>{content.retentionText}</p>
        </section>

        {/* Your Rights */}
        <section className="legal-section">
          <h2>{content.yourRights}</h2>
          <p>{content.yourRightsText}</p>
          <ul className="list-items">
            {content.rightsList.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </section>

        {/* Contact */}
        <section className="legal-section contact-section">
          <h2>{content.contact}</h2>
          <p>{content.contactText}</p>
          <div className="contact-info">
            <p>
              <strong>{content.email}:</strong> support@safrjal-otp.com
            </p>
            <p>
              <strong>{content.phone}:</strong> +962 795 021 746
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
